import { useEffect } from 'react';

import { Typography } from '@material-ui/core';

import {
  DialogCompositeMui as DialogComposite,
  EditButtonMui as EditButton,
  MultiStepDialogMui as MultiStepDialog,
  SidebarSectionMui as SidebarSection,
  useDisplayNames,
  useFormFieldsContext,
} from '@spotify/confidence-core-react';
import { useIdentityRef } from '@spotify/plugin-confidence-iam-react';
import {
  AboutSection,
  InputsSummary,
  MeasurementFormValues,
  PreviewSection,
  StepId,
  TypeSection,
  useEntity,
  useFactTable,
} from '@spotify/plugin-confidence-metrics-react';

type SummaryStepProps = {
  stepId?: string;
  loading?: boolean;
  goToStep?: (stepId: string) => void;
};

export const SummaryStep = ({
  stepId = StepId.Summary,
  loading,
  goToStep,
}: SummaryStepProps) => {
  const { values, setValues } = useFormFieldsContext<MeasurementFormValues>();
  const { setDisplayName } = useDisplayNames();

  const { entity } = useEntity({
    name: values.entity,
    skip: !values.entity,
  });
  const { factTable } = useFactTable({
    name: values.factTable,
    skip: !values.factTable,
  });
  const { identity: ownerIdentity } = useIdentityRef({
    name: values.owner,
    skip: !values.owner,
  });

  useEffect(() => {
    const names: Record<string, string> = {};
    if (entity?.name && entity?.displayName) {
      names[entity.name] = entity.displayName;
    }
    if (factTable?.name && factTable?.displayName) {
      names[factTable.name] = factTable.displayName;
    }
    if (ownerIdentity?.name && ownerIdentity?.displayName) {
      names[ownerIdentity.name] = ownerIdentity.displayName;
    }
    if (Object.keys(names).length > 0) {
      setDisplayName(names);
    }
  }, [entity, factTable, ownerIdentity, setDisplayName]);

  return (
    <MultiStepDialog.Step id={stepId}>
      <MultiStepDialog.Header>
        <Typography variant="h4" component="h4">
          Summary
        </Typography>
        <DialogComposite.Subtitle>
          Review your measurement before creating. You can edit it later.
        </DialogComposite.Subtitle>
      </MultiStepDialog.Header>
      <MultiStepDialog.Body>
        <PreviewSection
          value={{
            entity: values.entity,
            factTable: values.factTable,
            typeSpec: values.typeSpec,
            nullHandling: values.nullHandling,
            filter: values.filter,
          }}
        />

        <AboutSection
          value={{
            displayName: values.displayName,
            description: values.description,
            owner: values.owner,
          }}
          onChange={aboutValues => {
            setValues({
              ...aboutValues,
              displayNameManuallyEdited:
                aboutValues.displayName !== values.displayName ||
                values.displayNameManuallyEdited,
            });
          }}
         data-track-source-anchor="77561beab8;summary-step-about-section"/>

        <TypeSection
          metricType={values.metricType}
          action={
            goToStep && (
              <EditButton size="small" onClick={() => goToStep(StepId.Type)}  data-track-source-anchor="d21cad3030;summary-step-edit-button"/>
            )
          }
        />

        <SidebarSection.Root>
          <SidebarSection.Header>
            <SidebarSection.Title>Inputs</SidebarSection.Title>
            {goToStep && (
              <SidebarSection.Action>
                <EditButton
                  size="small"
                  onClick={() => goToStep(StepId.Inputs)}
                 data-track-source-anchor="1847d4796d;summary-step-edit-button-2"/>
              </SidebarSection.Action>
            )}
          </SidebarSection.Header>
          <SidebarSection.Content>
            <InputsSummary
              value={{
                entity: values.entity,
                factTable: values.factTable,
                filter: values.filter,
              }}
            />
          </SidebarSection.Content>
        </SidebarSection.Root>
      </MultiStepDialog.Body>
      <MultiStepDialog.Actions>
        <MultiStepDialog.Actions.Prev />
        <MultiStepDialog.Actions.Submit disabled={loading}>
          Create
        </MultiStepDialog.Actions.Submit>
      </MultiStepDialog.Actions>
    </MultiStepDialog.Step>
  );
};
